import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { post } from "@/lib/api-client";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { StatusPill } from "@/components/ui/StatusPill";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { PAYMENT_METHOD_LABELS } from "@/lib/constants";
import type { Payment } from "@/types/payment";

export function PaymentDetailModal({
  open, onClose, payment, onAction,
}: { open: boolean; onClose: () => void; payment: Payment; onAction: () => void }) {
  const [reason, setReason] = useState("");
  const [notes,  setNotes]  = useState("");

  const approve = useMutation({
    mutationFn: () => post<Payment>(`/payments/payments/${payment.payment_id}/approve`, { notes }),
    onSuccess: () => { toast.success("Payment approved"); onAction(); },
  });
  const reject = useMutation({
    mutationFn: () => post<Payment>(`/payments/payments/${payment.payment_id}/reject`, { reason }),
    onSuccess: () => { toast.success("Payment rejected"); onAction(); },
  });
  const resubmit = useMutation({
    mutationFn: () => post<Payment>(`/payments/payments/${payment.payment_id}/resubmit`, { reason }),
    onSuccess: () => { toast.success("Resubmission requested"); onAction(); },
  });

  const decided = payment.decision === "approved" || payment.decision === "rejected";

  return (
    <Modal open={open} onClose={onClose} title={`Payment ${payment.payment_id}`} size="lg">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Row label="Customer"    value={<span className="font-mono">{payment.customer_id}</span>} />
          <Row label="Purpose"     value={<Badge tone="info" className="capitalize">{payment.purpose}</Badge>} />
          <Row label="Method"      value={<span className="font-semibold text-surface-900">{payment.method}</span>} />
          <Row label="Sender Account" value={<span className="font-mono text-lg font-bold text-brand-700">{payment.sender_account || "—"}</span>} /> {/* 🛠️ Added */}
          <Row label="Reference"   value={<span className="font-mono text-xs bg-surface-100 px-2 py-1 rounded">{payment.transaction_reference}</span>} />
          <Row label="Amount"      value={<strong className="text-lg text-success-700">{formatCurrency(payment.amount)}</strong>} />
          <Row label="Payment date" value={formatDateTime(payment.payment_date)} />
          <Row label="Decision"    value={<StatusPill status={payment.decision} />} />
        </div>

        {payment.notes && (
          <div>
            <div className="text-2xs font-semibold uppercase text-surface-500">Customer notes</div>
            <div className="mt-1 text-sm text-surface-800 bg-surface-50 border border-surface-200 rounded-lg p-3 whitespace-pre-wrap">
              {payment.notes}
            </div>
          </div>
        )}

        {payment.screenshot && (
          <div>
            <div className="text-2xs font-semibold uppercase text-surface-500 mb-1">Payment screenshot</div>
            <a href={payment.screenshot} target="_blank" rel="noreferrer">
              <img src={payment.screenshot} alt="Payment proof"
                   className="rounded-lg border border-surface-200 max-h-96 object-contain" />
            </a>
          </div>
        )}

        {!decided && (
          <div className="border-t border-surface-200 pt-5 space-y-3">
            <div>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">
                Approval notes (optional)
              </label>
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full h-16 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">
                Rejection/resubmit reason (required for those actions)
              </label>
              <textarea
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Explain to the customer why…"
                className="w-full h-16 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" leftIcon={<RotateCcw className="h-4 w-4" />}
                      loading={resubmit.isPending}
                      disabled={!reason.trim()}
                      onClick={() => resubmit.mutate()}>
                Request resubmission
              </Button>
              <Button variant="danger" leftIcon={<XCircle className="h-4 w-4" />}
                      loading={reject.isPending}
                      disabled={!reason.trim()}
                      onClick={() => reject.mutate()}>
                Reject
              </Button>
              <Button leftIcon={<CheckCircle2 className="h-4 w-4" />}
                      loading={approve.isPending}
                      onClick={() => approve.mutate()}>
                Approve
              </Button>
            </div>
          </div>
        )}

        {decided && (
          <div className="border-t border-surface-200 pt-4 text-sm">
            <div className="text-2xs font-semibold uppercase text-surface-500">Decision notes</div>
            <div className="text-surface-800 mt-1">{payment.decision_notes || "—"}</div>
            <div className="text-2xs text-surface-500 mt-2">
              Decided {formatDateTime(payment.decided_at)}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-2xs font-semibold uppercase text-surface-500">{label}</div>
      <div className="mt-0.5 text-surface-900">{value}</div>
    </div>
  );
}
